import type {Build} from '../../../../services/rest'
import type {BuildTypeId, PipelineId, ProjectId} from '../../../../types'
import type {SelfHostedRequirement} from '../EditPipelinePage/SettingsSidebarContent/JobSettingsSidebar/JobAgentRequirements/SelfHosted/Requirements/SelfHostedRequirements.types'

export enum HTTPCodes {
  OK = 200,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  INTERNAL_SERVER_ERROR = 500,
  BAD_GATEWAY = 502,
}

export interface HTTPError {
  statusCode: number | undefined
  url: string | null | undefined
  response: Response | undefined
}

export enum ParameterNames {
  PIPELINE_HEAD = 'teamcity.internal.pipelines.isPipelineHead',
}

export enum StepTypes {
  CLI = 'script',
  MAVEN = 'maven',
  NODE = 'node-js',
  GRADLE = 'gradle',
}

export enum SuggestionType {
  CLI_TO_MAVEN = 'CliToMaven',
  CLI_TO_GRADLE = 'CliToGradle',
  CLI_DOCKER_LOGIN = 'CliDockerLogin',
  CLI_TO_WORKING_DIR = 'CliToWorkingDir',
  PARALLELISM = 'Parallelism',
  JDK_DOCKER_INFO = 'JdkDockerInfo',
}

export enum ReuseType {
  NONE = 'none',
  SUCCESSFUL = 'successful',
  SUCCESSFUL_OR_FAILED = 'successful-or-failed',
}

export enum FailureActionType {
  RUN_ADD_PROBLEM = 'run-add-problem',
  RUN_IGNORE_PROBLEM = 'run-ignore-problem',
  MARK_AS_FAILED_TO_START = 'mark-as-failed-to-start',
  CANCEL = 'cancel',
}
export type Step = {
  type?: string
  name?: string
  [key: string]: unknown
}

export type JobFeature = {
  type?: string
  [key: string]: unknown
}

type DependencySettings = {
  files?: readonly string[]
}

export type Dependency = string | Record<string, DependencySettings>

export type PipelineDependencySettings = {
  reuse?: ReuseType
  'enforce-revisions-synchronisation'?: boolean
  'on-failed-dependency'?: FailureActionType
  'on-incomplete-dependency'?: FailureActionType
}
export type PipelineDependencyDetails = {
  name?: string
  projectId?: ProjectId
  parentProjectId?: ProjectId
  isPipeline?: boolean
  build?: Build
}
export type PipelineDependencyId = BuildTypeId | PipelineId
export type PipelineDependency =
  | Record<PipelineDependencyId, PipelineDependencySettings>
  | PipelineDependencyId
type PipelineDependencies = PipelineDependency[]
export type DependencyGroup = {
  projectId?: ProjectId
  parentProjectId?: ProjectId
  isUnknown?: boolean
  items: {
    id: PipelineDependencyId
    name: string
    isPipeline?: boolean
    build?: Build
  }[]
}
export type Integration =
  | string
  | {
      id: string
      type: string
    }
export type Jobs = Record<string, Job>

export type JobRepositorySettings = {
  enabled: boolean
  path?: string
}

export type JobFile =
  | {
      path: string
      'share-with-jobs': boolean
      'publish-artifact': boolean
    }
  | string

export type Job = {
  steps?: Step[]
  features?: JobFeature[]
  name?: string
  dependencies?: Dependency[]
  integrations?: Integration[]
  repositories?: Record<string, JobRepositorySettings>[]
  'output-parameters'?: Record<string, string>
  parameters?: Record<string, string>
  'files-publication'?: JobFile[]
  'runs-on'?: RunsOn
  parallelism?: number
  'allow-reuse'?: boolean
  'enable-dependency-cache'?: boolean
}

export type RunsOn =
  | string
  | {
      ['self-hosted']: Array<SelfHostedRequirement>
    }

export type PipelineSettings = {
  jobs?: Jobs
  'import-parameters'?: string[]
  parameters?: Record<string, string>
  'output-parameters'?: Record<string, string>
  secrets?: Record<string, string>
  dependencies?: PipelineDependencies
}
